import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { apiFetch } from '../lib/api.ts';
import { dateOnly, money, statusClass, statusLabel } from '../lib/format.ts';

interface SalesReturnLine {
  lineNo: number;
  productCode: string | null;
  description: string | null;
  qty: number;
  unitPrice: number;
  subTotal: number;
  reason: string | null;
}

interface SalesReturnDetail {
  id: number;
  srNo: string;
  srDate: string;
  customerNo: string | null;
  currency: string;
  value: number;
  status: string;
  ref: string | null;
  lines: SalesReturnLine[];
}

export function SalesReturnDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ['sales-return', id],
    queryFn: () =>
      apiFetch<SalesReturnDetail>(
        `/api/dashboard/sales-returns/${encodeURIComponent(id ?? '')}`,
      ),
    enabled: !!id,
  });

  // Only real product (SKU) lines — OBM adds blank/free-text note rows.
  const productLines = (data?.lines ?? []).filter((l) => l.productCode);

  return (
    <>
      <Link to="/sales-returns" className="back-link">← All sales returns</Link>

      {isLoading && <div className="loading">Loading…</div>}
      {error && <div className="error">{(error as Error).message}</div>}

      {data && (
        <>
          <header className="main-header">
            <div>
              <h1 className="main-title">Return {data.srNo}</h1>
              <span className="main-subtitle mono">{dateOnly(data.srDate)}</span>
            </div>
            <span className={`badge ${statusClass(data.status)}`}>
              {statusLabel(data.status)}
            </span>
          </header>

          <section className="panel">
            <header className="panel-header">
              <h2 className="panel-title">Header</h2>
            </header>
            <dl className="detail-grid">
              <dt>SR no</dt>
              <dd className="mono">{data.srNo}</dd>
              <dt>Date</dt>
              <dd>{dateOnly(data.srDate)}</dd>
              <dt>Customer</dt>
              <dd>
                {data.customerNo ? (
                  <Link to={`/customers/${encodeURIComponent(data.customerNo)}`}>
                    {data.customerNo}
                  </Link>
                ) : (
                  '—'
                )}
              </dd>
              <dt>Reference</dt>
              <dd className="mono">{data.ref || '—'}</dd>
              <dt>Value</dt>
              <dd>{money(data.value, data.currency)}</dd>
            </dl>
          </section>

          <section className="panel">
            <header className="panel-header">
              <h2 className="panel-title">Line items</h2>
              <span className="panel-subtitle">
                {productLines.length} item{productLines.length === 1 ? '' : 's'}
              </span>
            </header>
            {productLines.length === 0 ? (
              <div className="empty">
                No priced SKU lines on file. Run <code>--mode L</code> on the agent to fetch{' '}
                <code>items_data</code> from OBM.
              </div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>SKU</th>
                    <th>Description</th>
                    <th className="num">Qty</th>
                    <th className="num">Unit price</th>
                    <th className="num">Subtotal</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {productLines.map((l) => (
                    <tr key={l.lineNo}>
                      <td className="mono">{l.lineNo}</td>
                      <td className="mono">
                        {l.productCode ? (
                          <Link to={`/skus/${encodeURIComponent(l.productCode)}`}>
                            {l.productCode}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>{l.description || '—'}</td>
                      <td className="num">{l.qty}</td>
                      <td className="num">{money(l.unitPrice, data.currency)}</td>
                      <td className="num">{money(l.subTotal, data.currency)}</td>
                      <td>{l.reason || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </>
  );
}
